
import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { StrategyProvider, StrategyWithDetails } from "@/types/strategy-types";
import BasicFields from "../fields/BasicFields";
import CheckboxFields from "../fields/CheckboxFields";
import { PerformanceFields } from "../fields/PerformanceFields";
import { TradeFields } from "../fields/TradeFields";
import { AdvancedFields } from "../fields/AdvancedFields";
import { FormActions } from "../FormActions";
import { Form } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";

const strategySchema = z.object({
  name: z.string().min(3, { message: "Name must be at least 3 characters" }),
  description: z.string().optional().nullable(),
  currency: z.string().min(1, { message: "Currency is required" }).default("USDT"),
  risk_level: z.coerce.number().min(1).max(5),
  plan: z.enum(["free", "premium"]).default("free"),
  min_investment: z.coerce.number().min(1, { message: "Minimum investment is required" }),
  exchange: z.string().min(1, { message: "Exchange is required" }),
  product: z.string().optional().nullable(),
  traded_symbols: z.array(z.string()).optional().nullable(),
  compounding: z.boolean().default(false),
  risk_corridor: z.boolean().default(false),
  verified_since: z.string().optional().nullable(),
  defined_max_drawdown: z.coerce.number().optional().nullable(),
  expected_monthly_profit: z.coerce.number().optional().nullable(),
  social_trading: z.array(z.string()).optional().nullable(),
  active: z.boolean().default(true),
  approved: z.boolean().default(false),
  strategy_provider_id: z.string().min(1, { message: "Provider is required" }),
});

type StrategyFormData = z.infer<typeof strategySchema>;

interface StrategyFormProps {
  onSubmit: (strategy: Partial<StrategyWithDetails>) => Promise<void>;
  initialData?: StrategyWithDetails;
  providers: StrategyProvider[];
  onCancel: () => void;
  userRole?: string | null;
}

const StrategyForm = ({
  onSubmit,
  initialData,
  providers,
  onCancel,
  userRole
}: StrategyFormProps) => {
  const { toast } = useToast();
  
  const providerIdForUser = userRole === 'strategy_provider' && providers.length === 1 
    ? providers[0].id 
    : undefined;

  const convertRiskLevelToNumber = (numericLevel?: number): number => {
    if (!numericLevel) return 3; // Medium risk as default
    return numericLevel;
  };

  const normalizePlan = (planValue?: string): "free" | "premium" => {
    return planValue === 'pro' || planValue === 'premium' ? 'premium' : 'free';
  };

  const defaultValues: Partial<StrategyFormData> = {
    name: initialData?.name || '',
    description: initialData?.description || '',
    currency: initialData?.currency || 'USDT',
    risk_level: convertRiskLevelToNumber(initialData?.risk_level),
    plan: normalizePlan(initialData?.plan),
    min_investment: initialData?.min_investment || undefined,
    exchange: initialData?.exchange || '',
    product: initialData?.product || undefined,
    traded_symbols: initialData?.traded_symbols || undefined,
    compounding: initialData?.compounding || false,
    risk_corridor: initialData?.risk_corridor || false,
    verified_since: initialData?.verified_since ? new Date(initialData.verified_since).toISOString().split('T')[0] : undefined,
    defined_max_drawdown: initialData?.defined_max_drawdown || undefined,
    expected_monthly_profit: initialData?.expected_monthly_profit || undefined,
    social_trading: initialData?.social_trading || undefined,
    active: initialData?.active ?? true,
    approved: initialData?.approved ?? false,
    strategy_provider_id: initialData?.strategy_provider_id || providerIdForUser || '',
  };

  const form = useForm<StrategyFormData>({
    resolver: zodResolver(strategySchema),
    defaultValues: defaultValues,
    mode: "onChange"
  });

  const handleSubmit = async (values: StrategyFormData) => {
    try {
      console.log("Form values before submission:", values);
      
      await onSubmit(values);
      toast({
        title: initialData ? "Strategy updated" : "Strategy created",
        description: initialData 
          ? "The strategy has been updated successfully." 
          : "The strategy has been created successfully."
      });
      onCancel();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "There was a problem saving the strategy. Please try again.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (userRole === 'strategy_provider' && providers.length === 1) {
      const providerId = providers[0].id;
      const currentProviderId = form.getValues('strategy_provider_id');
      
      if (!currentProviderId || currentProviderId === '') {
        form.setValue('strategy_provider_id', providerId);
      }
    }
  }, [userRole, providers, form]);

  console.log("Form values:", form.getValues());
  console.log("Form errors:", form.formState.errors);
  console.log("Provider ID for user:", providerIdForUser);
  console.log("User role:", userRole);
  console.log("Providers:", providers);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <BasicFields 
          form={form} 
          providers={providers} 
          isProvider={userRole === 'strategy_provider'}
        />
        
        <PerformanceFields form={form} />
        
        <TradeFields form={form} />
        
        <AdvancedFields form={form} />
        
        <CheckboxFields 
          form={form} 
          isAdmin={userRole === 'admin'}
        />
        
        <FormActions 
          onCancel={onCancel} 
          isSubmitting={form.formState.isSubmitting} 
          isEdit={!!initialData}
        />
      </form>
    </Form>
  );
};

export default StrategyForm;
